import { EntityName } from 'shared/domain/entityName';
import { filterOutSuperAdmin } from 'shared/domain/user/filterSuperAdmin';
import { userModelToUserOnView } from 'shared/domain/user/mapping/toView';
import { userClient } from 'shared/domain/user/userClient';
import React, { PropsWithChildren, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useEntityById } from 'store/entityCollection';
import { useProjectOrganizationId } from 'store/project';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { UserOnView } from 'shared/domain/user/types/view';
import { useSites } from 'store/site';
import { useProcesses } from 'store/process';
import { useProjectUsers } from 'store/projectUsers';

interface UserContextType {
  user: UserOnView | undefined;
  loading: boolean;
}

export const UserContext = React.createContext<UserContextType>({
  user: undefined,
  loading: true,
});

/** The user of the URL, with their access to sites and processes; never a super admin. */
const WithUser: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const id = useAppUrlParams(UrlParamName.USER_ID);
  const organizationId = useProjectOrganizationId();
  const { processes } = useProcesses();
  const { items: sites, loading: loadingSites } = useSites();
  const { items: users } = useProjectUsers();
  const intl = useIntl();
  const { item: userModel, loading: loadingUser } = useEntityById({
    id,
    client: userClient,
    items: users,
    entityName: EntityName.users,
  });

  const ctx = useMemo((): UserContextType => {
    const loading = loadingUser || loadingSites;
    const isVisibleUser = userModel && filterOutSuperAdmin(userModel);
    return {
      user:
        isVisibleUser && !loading
          ? userModelToUserOnView({ userModel, sites, processes, intl, organizationId })
          : undefined,
      loading,
    };
  }, [userModel, loadingUser, loadingSites, sites, processes, intl, organizationId]);

  return (
    <UserContext.Provider value={ctx}>{children}</UserContext.Provider>
  );
};

export const withUser =
  (Component: React.ComponentType<any>) =>
  ({ ...props }): React.ReactElement => (
    <WithUser>
      <Component {...props} />
    </WithUser>
  );

export function useUser(): UserContextType {
  const context = React.useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserContextProvider');
  }
  return context;
}
